import React from 'react';
import { chartTheme } from './Theme';
import { niceTicks, formatInr } from './utils';

// series: [{ name: string, color: string, data: [{ date: string, amount: number }] }]
const MultiLineChart = ({ series = [], width = '100%', height = 180, yCurrency = false, dark = false }) => {
  const w = 700;
  const h = Number(height) || 180;

  const allData = (series || []).flatMap(s => s.data || []);
  const values = allData.map(d => Number(d.amount ?? d.value ?? 0));
  const allZero = values.length === 0 || values.every(v => (v || 0) === 0);

  // Padding: ensure y-axis labels are always visible
  const padLeft = yCurrency ? 74 : (dark ? 54 : chartTheme.layout.pad);
  const padRight = 18;
  const padTop = 18;
  const padBottom = 22;

  const maxVal = Math.max(1, ...values);
  const { ticks, max } = niceTicks(maxVal, chartTheme.layout.yTicks);

  const axisColor = dark ? 'rgba(255,255,255,0.35)' : chartTheme.colors.axis;
  const gridColor = dark ? 'rgba(255,255,255,0.06)' : chartTheme.colors.grid;
  const textColor = dark ? 'rgba(255,255,255,0.65)' : chartTheme.colors.axis;
  const bgColor = dark ? 'transparent' : '#ffffff';
  const noDataColor = dark ? 'rgba(255,255,255,0.4)' : '#6b7280';

  const formatYLabel = (v) => {
    const n = Number(v || 0);
    if (!Number.isFinite(n)) return '';
    if (!yCurrency) return n.toFixed(0);
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n.toFixed(0)}`;
  };

  if (allZero) {
    return (
      <div style={{ width: '100%', minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: noDataColor, fontSize: 11 }}>
        No data
      </div>
    );
  }

  const uniqDates = (() => {
    const set = new Set();
    for (const s of (series || [])) for (const d of (s.data || [])) set.add(String(d.date ?? ''));
    return Array.from(set).filter(Boolean).sort();
  })();

  const scaleX = (i) => padLeft + i * ((w - padLeft - padRight) / Math.max(1, uniqDates.length - 1));
  const scaleY = (v) => h - padBottom - ((v) / (max || 1)) * (h - padTop - padBottom);

  const buildPoints = (s) => {
    const map = Object.fromEntries((s.data || []).map(d => [String(d.date ?? ''), Number(d.amount ?? d.value ?? 0)]));
    return uniqDates.map((dt, i) => `${scaleX(i)},${scaleY(map[dt] || 0)}`).join(' ');
  };

  const gradId = (c) => `mlcGrad-${String(c || '').replace('#', '')}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={width} height="100%" style={{ display: 'block' }}>
      <rect x="0" y="0" width={w} height={h} fill={bgColor} />

      <line x1={padLeft} y1={h - padBottom} x2={w - padRight} y2={h - padBottom} stroke={axisColor} strokeWidth="1" />
      {ticks.map((t, i) => {
        const y = scaleY(t);
        return (
          <g key={i}>
            <line x1={padLeft} y1={y} x2={w - padRight} y2={y} stroke={gridColor} strokeWidth="1" />
            <text x={padLeft - 8} y={y + 4} textAnchor="end" fontSize={dark ? 11 : chartTheme.fonts.tickSize} fill={textColor}>
              {formatYLabel(t)}
            </text>
          </g>
        );
      })}

      <defs>
        {(series || []).map((s, idx) => (
          <linearGradient key={idx} id={gradId(s.color)} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity={dark ? 0.22 : 0.12} />
            <stop offset="100%" stopColor={s.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>

      {(series || []).map((s, idx) => {
        const pts = buildPoints(s);
        const map = Object.fromEntries((s.data || []).map(d => [String(d.date ?? ''), Number(d.amount ?? d.value ?? 0)]));
        return (
          <g key={idx}>
            <polygon
              points={`${scaleX(0)},${h - padBottom} ${pts} ${scaleX(uniqDates.length - 1)},${h - padBottom}`}
              fill={`url(#${gradId(s.color)})`}
            />
            <polyline
              points={pts}
              fill="none"
              stroke={s.color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {uniqDates.map((dt, i) => {
              const v = map[dt] || 0;
              return (
                <circle
                  key={i}
                  cx={scaleX(i)}
                  cy={scaleY(v)}
                  r="3"
                  fill={s.color}
                  stroke={dark ? 'rgba(0,0,0,0.28)' : '#fff'}
                  strokeWidth="1.5"
                >
                  <title>{`${dt}: ${yCurrency ? formatInr(v) : Number(v || 0).toFixed(0)} • ${s.name}`}</title>
                </circle>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

export default MultiLineChart;


